import React, { useEffect, useState } from 'react';
import Paragraph from 'src/elements/typography/paragraph';
import { palette } from 'src/styles/colors';
import { useFormResultController } from 'src/hooks/useFormResultController';
import FormResult from './FormResult';
import ButtonPrimary from 'src/elements/buttons/2023_Q4/ButtonPrimary';
import {
  FooterFormContainer,
  FormInputsContainer,
  ActionContainer,
  FooterFormPosition,
  FooterHubspotForm,
} from './FooterForm.styles';
import { createHubSpotForm, submitHubSpotFormBuilder } from 'src/functions/hubspot/hubspot.service';
import { LinkProps } from 'src/elements/links/2023_Q4/LinkBranded';
import { LayoutTheme } from 'src/elements/Layout/2023_Q4/Layout';

const submitHubSpotForm = submitHubSpotFormBuilder('#hubspotFooterForm');

interface FooterFormProps {
  callToActionColorScheme?: LinkProps['colorScheme'];
  theme?: LayoutTheme;
}

const FooterForm = ({ callToActionColorScheme = 'Sunshine', theme }: FooterFormProps): JSX.Element => {
  const [formResult, setFormResult] = useState<null | 'failure' | 'success'>(null);
  const [buttonIsBlocked, setButtonIsBlocked] = useState(false);
  const [legalText, setLegalText] = useState('');

  const { isEmailSubscriptionSubmitted, setFormSubmission } = useFormResultController();

  const titleCopy = () => {
    if (theme === 'pro') {
      return (
        <span>
          <b>16 Lessons from Real Mighty Pro Hosts</b> <br />
          Delivered right to your inbox—they’ll help you get concrete about the possibilities for your business
        </span>
      );
    }

    return (
      <span>
        <b>Build a $1M Community in 9 SIMPLE STEPS.</b> <br />
        This free masterclass went viral—sign up to learn why
      </span>
    );
  };

  const buttonCopy = theme === 'pro' ? 'Send Me the Secrets' : 'Unlock the FREE Masterclass';
  const successCopy = theme === 'pro' ? 'Success—the first tips are on their way to your inbox' : null;

  useEffect(() => {
    if (isEmailSubscriptionSubmitted()) {
      setFormResult('success');
      return;
    }

    // Form ID same as Profit Sales Email Popup
    const formId = theme === 'pro' ? 'b5c092d9-163f-47b1-9fc3-7a5b6c953a64' : '79e916cb-d4f8-4ea5-990c-ee51c8259e7b';
    createHubSpotForm({
      formContainer: '#hubspotFooterForm',
      formId,
      onFormReadyCallback: () => {
        const legalTag = document.getElementsByClassName('legal-consent-container') as HTMLCollectionOf<HTMLDivElement>;
        if (legalTag) {
          const legalText = legalTag[0]?.innerText || '';
          const defaultLegalText = `By signing up you're opting in to emails from us about the free masterclass, our podcast, and other helpful guides and goodies.  You can unsubscribe at any time.`;
          setLegalText(legalText || defaultLegalText);
        }
      },
      onFormSubmitCallback: () => {
        setButtonIsBlocked(true);
      },
      onFormSubmittedCallback: () => {
        setFormResult('success');
        setFormSubmission();
      },
      errorCallback: () => {
        const failureTimeout = setTimeout(() => {
          setFormResult('failure');
          setButtonIsBlocked(true);
          clearTimeout(failureTimeout);
        }, 2000);
      },
    });
  }, [isEmailSubscriptionSubmitted]);

  return (
    <FooterFormPosition className='footer-form-container'>
      {!formResult && (
        <FooterFormContainer
          className='footer-email-capture'
          id='email-capture-container'
        >
          <FormInputsContainer>
            <Paragraph
              standardSize='1'
              color={palette.white}
              align='left'
              className='footer-form-title'
            >
              {titleCopy()}
            </Paragraph>
            <FooterHubspotForm id='hubspotFooterForm' />
          </FormInputsContainer>
          <ActionContainer>
            <ButtonPrimary
              text={buttonCopy}
              disabled={buttonIsBlocked}
              type='submit'
              customBorderRadius={32}
              colorScheme={callToActionColorScheme}
              onClick={submitHubSpotForm}
            />
            <Paragraph
              text={legalText}
              standardSize='0.625'
              color={palette.white}
              align='left'
            />
          </ActionContainer>
        </FooterFormContainer>
      )}
      {formResult && (
        <FormResult
          result={formResult}
          successText={successCopy}
        />
      )}
    </FooterFormPosition>
  );
};

export default FooterForm;
